import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import NoteIcon from '../svg/NoteIcon';
import ProductImage from '../products/ProductImage';
import OrderStatus from './OrderStatus';
import { formatPrice } from '@/utils/misc';
import generalSettingsStore from '@/stores/generalSettingsStore';
import { formatDateTime } from '@/utils/dateFormat';

const OrderDetails = inject('studioStore', 'wooCommerceStore')(observer(({
  wooCommerceStore,
  order,
  showTitle = true,
}) => {
  const t = useTranslations('Account');
  const tu = useTranslations('Units');
  const dateFormat = generalSettingsStore.dateFormat;

  // Helper to check if item has a discount
  const hasDiscount = (item) => {
    return parseFloat(item.subtotal) > parseFloat(item.total);
  };

  // Helper to format quantity display based on product type
  const formatQuantityDisplay = (item) => {
    // Find product meta data
    const metadata = item.meta_data || [];
    const isUnit = metadata.find(meta => meta.key === '_units_option')?.value === 'Yes';
    const isPack = metadata.find(meta => meta.key === '_pack_option')?.value === 'Yes';
    const isWeight = metadata.find(meta => meta.key === '_weight_option')?.value === 'Yes';
    const productWeight = metadata.find(meta => meta.key === '_weight')?.value || 0;

    if (isUnit) {
      return `${item.quantity} ${item.quantity === 1 ? tu('unit') : tu('units')}`;
    } else if (isPack) {
      return `${item.quantity} ${item.quantity === 1 ? tu('pack') : tu('packs')}`;
    } else if (isWeight && productWeight) {
      // Calculate total weight (weight * quantity)
      const totalWeight = parseFloat(productWeight) * parseFloat(item.quantity);
      return `${totalWeight.toFixed(2)} ${tu('kg')}`;
    } else {
      return `${t('quantity')}: ${item.quantity}`;
    }
  };

  // Helper to get item note if exists
  const getItemNote = (item) => {
    const metadata = item.meta_data || [];
    return metadata.find(meta => meta.key === '_line_item_note')?.value || null;
  };

  return (
    <div>
      {showTitle && (
        <>
            <h2 className="text-2xl font-secondary font-semibold text-primary-studio mb-6">
                {t('order_details')} #{order.number}
            </h2>
            <hr className="dotted-spaced my-4" />
        </>
      )}

      <div className="flex justify-between items-baseline mb-2">
        {/* Order Date */}
        <div className="my-3">
          <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-2">{t('order_date')}</h3>
          <p className="text-secondary-text-studio">{formatDateTime(order.date_created, dateFormat)}</p>
        </div>
        {/* Payment Method */}
        <div className="my-3">
          <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-2">{t('payment_method')}</h3>
          <p className="text-secondary-text-studio">{order.payment_method_title}</p>
        </div>
        {/* Order Status */}
        <div className="my-3">
          <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-2">{t('order_status')}</h3>
          <div className="text-secondary-text-studio"><OrderStatus status={order.status} /></div>
        </div>
      </div>
      <hr className="dotted-spaced my-4" />

      <div className="flex justify-between items-baseline mb-2">
        {/* Shipping Details */}
        <div className="my-3">
          <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-2">{t('shipping_details')}</h3>
          <div className="text-secondary-text-studio">
            <p>{order.shipping.first_name} {order.shipping.last_name}</p>
            <p>{order.shipping.address_1}</p>
            {order.shipping.address_2 && <p>{order.shipping.address_2}</p>}
            {<p>{order.shipping.city}</p>}
            {/* <p>{order.shipping.city}, {order.shipping.state}</p> */}
            {/* <p>{order.shipping.country}</p> */}
            {order.meta_data?.find(meta => meta.key === '_shipping_address_notes')?.value && (
              <div className="mt-2">
                <p className="font-semibold text-primary-studio">{t("address_notes")}:</p>
                <p className="whitespace-pre-line">{order.meta_data.find(meta => meta.key === '_shipping_address_notes').value}</p>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Time Slot */}
        {order.meta_data?.find(meta => meta.key === 'selected_slot')?.value && (
          <div className="my-3">
            <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-2">{t('delivery_time')}</h3>
            <div className="text-secondary-text-studio">
              <p>
                {formatDateTime(order.meta_data.find(meta => meta.key === 'selected_slot').value.date, dateFormat)}
              </p>
              <p>
                {order.meta_data.find(meta => meta.key === 'selected_slot').value.slot.start_time.slice(0, 5)} -
                {order.meta_data.find(meta => meta.key === 'selected_slot').value.slot.end_time.slice(0, 5)}
              </p>
            </div>
          </div>
        )}
        {/* Delivery preferences */}
        {(order.meta_data?.find(meta => meta.key === 'replacement_preference')?.value ||
          order.meta_data?.find(meta => meta.key === 'receiving_preference')?.value) && (
          <div className="my-3">
            <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-2">{t('delivery_prefrenses')}</h3>

            {/* Product Replacement Preference */}
            {order.meta_data?.find(meta => meta.key === 'replacement_preference')?.value && (
              <div className="text-secondary-text-studio mb-4">
                <p className="font-semibold mb-1 text-primary-studio">
                  {t("product_replacement")}:
                </p>
                <p>
                  {t(order.meta_data?.find(meta => meta.key === 'replacement_preference')?.value)}
                </p>
              </div>
            )}

            {/* Receiving Shipment Preference */}
            {order.meta_data?.find(meta => meta.key === 'receiving_preference')?.value && (
              <div className="text-secondary-text-studio">
                <p className="font-semibold mb-1 text-primary-studio">
                  {t("receiving_shipment")}:
                </p>
                <p>
                  {t(order.meta_data?.find(meta => meta.key === 'receiving_preference')?.value)}
                </p>
              </div>
            )}

            {/* Customer Note */}
            {order.meta_data?.find(meta => meta.key === 'customer_note')?.value && (
              <div className="text-secondary-text-studio mt-4">
                <p className="font-semibold mb-1 text-primary-studio">
                  {t("customer_note")}:
                </p>
                <p>
                  {order.meta_data?.find(meta => meta.key === 'customer_note')?.value}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <hr className="dotted-spaced my-4" />

      {/* Products */}
      <div className="mb-6">
        <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-4">{t('products')}</h3>
        <div className="space-y-4">
          {order.line_items.map((item) => {
            const img = (item?.image?.src) ? item.image : {};
            const note = getItemNote(item);

            return (
              <>
                <div key={item.id} className="flex justify-between items-center">
                  <div className="flex items-center">
                    <ProductImage
                      img={img}
                      productName={item?.name}
                      width={64}
                      height={64}
                      className="h-16 w-16 object-contain rounded-lg me-2"
                    />
                    <div>
                      <p className="font-medium text-primary-studio">{item.name}</p>
                      <p className="text-sm text-secondary-text-studio">
                        {formatQuantityDisplay(item)}
                      </p>
                      {note && (
                        <p className="text-sm italic mt-1 text-gray-600 flex items-center">
                          <NoteIcon className="w-3 h-3 me-1 flex-shrink-0" />
                          <span>{note}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {hasDiscount(item) ? (
                      <>
                        <p className="font-medium text-secondary-studio">
                          {formatPrice(item.total, wooCommerceStore)}
                        </p>
                        <p className="text-xs text-secondary-text-studio line-through">
                          {formatPrice(item.subtotal, wooCommerceStore)}
                        </p>
                      </>
                    ) : (
                      <p className="font-medium text-primary-studio">
                        {formatPrice(item.total, wooCommerceStore)}
                      </p>
                    )}
                  </div>
                </div>
                <hr className="dotted-spaced my-4" />
              </>
            );
          })}
        </div>
      </div>

      {/* Totals */}
      <div className="mb-6">
        <h3 className="text-lg font-secondary font-semibold text-primary-studio mb-4">{t('summary')}</h3>
        <div className="flex items-center mb-2">
          <span className="text-secondary-text-studio">{t('sub_total')}</span>
          <span className="dotted-spaced grow mx-2"></span>
          <span className="font-medium text-primary-studio">
            {formatPrice(order.total - order.shipping_total, wooCommerceStore)}
          </span>
        </div>
        <div className="flex items-center mb-2">
          <span className="text-secondary-text-studio">{t('shipping_total')}</span>
          <span className="dotted-spaced grow mx-2"></span>
          <span className="font-medium text-primary-studio">
            {formatPrice(order.shipping_total, wooCommerceStore)}
          </span>
        </div>
        {order.discount_total > 0 && (
          <div className="flex items-center mb-2">
            <span className="text-secondary-text-studio">{t('discount')}</span>
            <span className="dotted-spaced grow mx-2"></span>
            <span className="font-medium text-primary-studio">
              -{formatPrice(order.discount_total, wooCommerceStore)}
            </span>
          </div>
        )}
        {/* Individual coupon lines */}
        {order.coupon_lines.map((coupon) => (
          <div key={`discount-${coupon.id}`} className="flex items-center">
            <span>{t("coupon")}: {coupon.code}</span>
            <span className="dotted-spaced grow mx-2"></span>
            <span className="text-secondary-studio">-{formatPrice(parseFloat(coupon.discount || 0))}</span>
          </div>
        ))}
        <div className="flex items-center text-lg font-semibold">
          <span className="text-primary-studio">{t('total')}</span>
          <span className="dotted-spaced grow mx-2"></span>
          <span className="text-primary-studio">
            {formatPrice(order.total, wooCommerceStore)}
          </span>
        </div>
      </div>
    </div>
  );
}));

OrderDetails.displayName = 'OrderDetails';

export default OrderDetails;